import React from 'react';
import { Button } from '@mui/material';
import { IItemListResponse } from 'srs.sharedcomponents/lib/esm/models/responses/srs.itemListResponse.model';
import {
    updateisMultiSelectFiltersApplied,
    updateMultiSelectFilters,
    updateFilters as updateFiltersRedux,
} from 'srs.sharedcomponents/lib/esm/redux/slices/productlistSlice';

export interface VehicleData {
    make: Record<string, string[]>;
    model: Record<string, string[]>;
    year: Record<string, string[]>;
    component: Record<string, string[]>;
}

export type SelectedValues = {
    make: string[];
    year: string[];
    model: string[];
    component: string[];
    supplier: string[];
    category: string[];
};

interface UpdateFiltersParams {
    dispatchFilters: any;
    onSale: boolean;
    newProduct: boolean;
    priceRange: number[];
    allCategories: any[];
    newSelectedValues: SelectedValues;
    callForPricing: boolean;
    updatedFilterItems: IItemListResponse | undefined;
    isPriceRangeSelected: boolean;
    priceRangeValues: any;
    searchedLabel: any;
    setIsInitialPageLoad: any;
    subSearch: any;
    searchParam: string;
    filtersStored: SelectedValues | undefined;
    previousMinPrice: number;
    previousMaxPrice: number;
    allInitialCategoriesWithChildren: any[];
    allInitialSuppliers: string[];
}

interface Category {
    CategoryName: string;
    SubCategories: Category[];
}

export const ActionButton = ({
    onClick,
    children,
    buttonType,
    buttonId,
}: {
    onClick: () => void;
    children: React.ReactNode;
    buttonType: string;
    buttonId: string;
}) => (
    <Button
        style={{
            marginTop: 1,
            fontSize: '12px',
            fontWeight: '900',
            color: buttonType === 'selectAll' ? 'white' : 'rgb(14, 22, 100)',
            backgroundColor:
                buttonType === 'selectAll' ? 'rgb(14, 22, 100)' : 'white',
            border: '1px solid',
            borderColor: 'rgb(14, 22, 100) !important',
            padding: '5px',
            textTransform: 'capitalize',
            borderRadius: 5,
        }}
        id={buttonId}
        onClick={onClick}
        data-testid={
            buttonType === 'selectAll' ? 'selectAllButton' : 'clearAllButton'
        }
    >
        {children}
    </Button>
);

export const findParentCategory = (categories: any[], value: string): any => {
    for (const category of categories) {
        if (category.code === value || category.label === value) {
            return category;
        }
        if (
            category.items?.some(
                (item: { code: string; label: string }) =>
                    item.code === value || item.label === value
            )
        ) {
            return category;
        }

        if (category.items) {
            const found = findParentCategory(category.items, value);
            if (found) {
                return category;
            }
        }
    }
    return null;
};

export function transformCategories(
    allCategories: any,
    selectedCategories: any,
    searchedCategoryLabel: string
) {
    const codesToNotAddCategoryHierarchy = [
        'Make_',
        'Year_',
        'Model_',
        'Component_',
    ];

    const findCategoryByCode = (categories: any, code: string): any => {
        for (let category of categories) {
            if (
                category.items?.some(
                    (item: { code: string }) => item.code === code
                )
            ) {
                return category;
            }
            if (category.items && category.items.length > 0) {
                const found = findCategoryByCode(category.items, code);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    const findCategoryHierarchy = (categories: any, code: any): any => {
        for (let category of categories) {
            if (category.code === code) {
                let result: { CategoryName: string; SubCategories: any[] } = {
                    CategoryName: category.label,
                    SubCategories: [],
                };
                if (category.items && category.items.length > 0) {
                    result.SubCategories =
                        buildHierarchy(category.items, selectedCategories) ||
                        [];
                }
                return result;
            }

            if (category.items) {
                const subCategoryHierarchy = findCategoryHierarchy(
                    category.items,
                    code
                );
                if (subCategoryHierarchy) {
                    return {
                        CategoryName: category.label,
                        SubCategories: [subCategoryHierarchy],
                    };
                }
            }
        }
        return null;
    };
    const extractCode = (item: { code: any }) => item.code;

    const extractCodesFromItem = (item: {
        items?: { code: string }[];
    }): string[] => {
        return item.items?.map((subItem) => subItem.code) || [];
    };

    const extractAllChildCodes = (parentCategory: {
        items?: { items?: { code: string }[] }[];
    }) => {
        return (
            parentCategory.items?.flatMap((item) =>
                extractCodesFromItem(item)
            ) || []
        );
    };
    const areAllChildrenSelected = (
        childCodes: any[],
        selectedCategories: string | any[]
    ) => {
        return childCodes.every((childCode: any) =>
            selectedCategories.includes(childCode)
        );
    };

    function shouldSkipSpecificCategory(code: string): boolean {
        return codesToNotAddCategoryHierarchy.some((text) =>
            code.includes(text)
        );
    }

    const buildHierarchy = (categories: any, selectedCategories: any[]) => {
        const result: any[] = [];
        const processedParents = new Set();
        const allParents = selectedCategories.map(
            (code: string) => findParentCategory(categories, code)?.label
        );
        if (
            !allParents.includes(searchedCategoryLabel) &&
            searchedCategoryLabel != ''
        ) {
            result.push({
                CategoryName: searchedCategoryLabel,
                SubCategories: [],
            });
        }

        selectedCategories.forEach((code: string) => {
            const handleCategoryAllSelection = () => {
                const parentCategory = findCategoryByCode(categories, code);
                if (!parentCategory) return;

                const parentCode = parentCategory.label;
                if (processedParents.has(parentCode)) return;

                const allChildCodes =
                    parentCategory.items?.map(extractCode) ?? [];
                const allChildrenSelected = areAllChildrenSelected(
                    allChildCodes,
                    selectedCategories
                );
                const shouldSkip = shouldSkipSpecificCategory(code);

                if (shouldSkip && !allChildrenSelected) return;

                if (allChildrenSelected) {
                    addParentCategoryToResult(Codelevels, parentCategory);
                    processedParents.add(parentCode);
                } else {
                    addCategoryHierarchyToResult(categories, code);
                }
            };

            const addParentCategoryToResult = (
                Codelevels: string | any[],
                parentCategory: { label: any }
            ) => {
                if (Codelevels.length <= 2) {
                    result.push({
                        CategoryName: parentCategory.label,
                        SubCategories: [],
                    });
                } else if (Codelevels.length === 3) {
                    result.push({
                        CategoryName: Codelevels[0],
                        SubCategories: [
                            {
                                CategoryName: parentCategory.label,
                                SubCategories: [],
                            },
                        ],
                    });
                }
            };

            const addCategoryHierarchyToResult = (
                categories: any,
                code: string
            ) => {
                const categoryHierarchy = findCategoryHierarchy(
                    categories,
                    code
                );
                if (categoryHierarchy) {
                    result.push(categoryHierarchy);
                }
            };

            const Codelevels = code.split('_');
            if (Codelevels.length == 2) {
                handleCategoryAllSelection();
            } else if (Codelevels.length === 3) {
                const parentCategory = findParentCategory(categories, code);

                if (parentCategory) {
                    if (processedParents.has(parentCategory.label)) return;
                    const parentCode = parentCategory?.label;
                    const allChildCodes = extractAllChildCodes(parentCategory);

                    const allChildrenSelected = allChildCodes.every(
                        (childCode: string) =>
                            selectedCategories.includes(childCode)
                    );

                    if (
                        allChildrenSelected &&
                        parentCode &&
                        !processedParents.has(parentCode)
                    ) {
                        result.push({
                            CategoryName: parentCategory.label,
                            SubCategories: [],
                        });
                        processedParents.add(parentCode);
                    } else {
                        handleCategoryAllSelection();
                    }
                }
            } else {
                // Fallback for codes that don't follow the underscore format
                addCategoryHierarchyToResult(categories, code);
            }
        });
        return result;
    };

    return buildHierarchy(allCategories, selectedCategories);
}

export const checkFacetTrimmingCatalog = (
    newSelectedValues: SelectedValues,
    allCategories: any[]
): boolean => {
    const reactAppFacetTrimingCatalog =
        process.env.REACT_APP_FACET_TRIMING_CATALOG;

    if (
        !reactAppFacetTrimingCatalog ||
        reactAppFacetTrimingCatalog.trim() === ''
    ) {
        return false;
    }

    const facetTrimingCatalogNames = reactAppFacetTrimingCatalog
        .split(',')
        .map((s: string) => s.trim().replace(/^'|'$/g, ''));

    // Find root categories whose label matches the facet trimming catalog
    const facetTrimingRoots = allCategories.filter((cat: any) =>
        facetTrimingCatalogNames.some((name: string) => cat.label === name)
    );

    if (facetTrimingRoots.length === 0) return false;

    // Collect all leaf codes under the facet trimming root categories
    const collectLeafCodes = (category: any): string[] => {
        if (!category.items || category.items.length === 0) {
            return [category.code];
        }
        return category.items.flatMap((item: any) => collectLeafCodes(item));
    };

    const facetTrimingCodes = new Set(
        facetTrimingRoots.flatMap((root: any) => collectLeafCodes(root))
    );

    const isESST = newSelectedValues.category.some((code: string) =>
        facetTrimingCodes.has(code)
    );

    if (
        isESST &&
        (newSelectedValues.make.length > 0 ||
            newSelectedValues.year.length > 0 ||
            newSelectedValues.model.length > 0 ||
            newSelectedValues.component.length > 0)
    ) {
        return true;
    }

    return false;
};

const getEffectivePriceRange = (
    isPriceRangeSelected: boolean,
    priceRangeValues: any,
    updatedFilterItems: IItemListResponse | undefined
): [number, number] => {
    const min = isPriceRangeSelected
        ? priceRangeValues[0]
        : updatedFilterItems?.minPrice;
    const max = isPriceRangeSelected
        ? priceRangeValues[1]
        : updatedFilterItems?.maxPrice;
    return [min, max];
};

export function getAllLeafCategoryCodes(categories: any[]): string[] {
    const codes: string[] = [];
    for (const cat of categories) {
        if (cat.items && cat.items.length > 0) {
            codes.push(...getAllLeafCategoryCodes(cat.items));
        } else if (cat.code) {
            codes.push(cat.code);
        }
    }
    return codes;
}

export function isCategoriesAllOrNone(
    selectedCategories: string[],
    allCategories: any[]
): boolean {
    if (selectedCategories.length === 0) return true;
    const allLeafCodes = getAllLeafCategoryCodes(allCategories);
    return allLeafCodes.every((code) => selectedCategories.includes(code));
}

export function isOnSaleSelectAllEquivalent(
    params: UpdateFiltersParams
): boolean {
    if (params.searchParam?.toLowerCase() !== 'on sale') return false;

    const allAvailableSuppliers = params.allInitialSuppliers ?? [];

    const { supplier, category, make, year, model, component } =
        params.newSelectedValues;

    const suppliersAllOrNone =
        supplier.length === 0 ||
        supplier.length >= allAvailableSuppliers.length;
    const categoriesOk = isCategoriesAllOrNone(
        category,
        params.allInitialCategoriesWithChildren ?? []
    );
    const noEsstFilters =
        make.length === 0 &&
        year.length === 0 &&
        model.length === 0 &&
        component.length === 0;
    const noToggles = !params.newProduct && !params.callForPricing;

    return suppliersAllOrNone && categoriesOk && noEsstFilters && noToggles;
}

export const updateFilters = async (
    params: UpdateFiltersParams,
    isVehicleApplicationClicked: boolean
) => {
    if (!isVehicleApplicationClicked) {
        const selectedValuesStore = buildSelectedValuesStore(
            params.filtersStored
        );

        const [minimumPrice, maximumPrice] = getEffectivePriceRange(
            params.isPriceRangeSelected,
            params.priceRangeValues,
            params.updatedFilterItems
        );

        const shouldApply = hasActiveFilters(
            params.newSelectedValues,
            selectedValuesStore,
            params,
            minimumPrice,
            maximumPrice
        );


    if (!shouldApply) {

      resetFilters(params.dispatchFilters);
      return;

    }

    const hasPriceFilter =
      (params.priceRange[0] !== minimumPrice && params.priceRange[0] != -1) ||
      (params.priceRange[1] !== maximumPrice && params.priceRange[1] != 1000000);

    if (!hasPriceFilter && isOnSaleSelectAllEquivalent(params)) {
      params.dispatchFilters(updateFiltersRedux({
        searchedValue: params.searchParam,
        selectedFilters: {
          make: [],
          year: [],
          model: [],
          component: [],
          supplier: [],
          category: [],
          isOnSaleClicked: false,
          isNewClicked: false,
          isCallForPricingClicked: false,
          minPrice: 0,
          maxPrice: 0,
          previousMinPrice: 0,
          previousMaxPrice: 0,
        },
        apiPayload: null,
      }));
      await resetFilters(params.dispatchFilters);
      return;
    }


    if (
      !hasPriceFilter &&
      !params.onSale &&
      !params.newProduct &&
      !params.callForPricing &&
      params.newSelectedValues.supplier.length === 0 &&
      params.newSelectedValues.make.length === 0 &&
      params.newSelectedValues.year.length === 0 &&
      params.newSelectedValues.model.length === 0 &&
      params.newSelectedValues.component.length === 0 &&
      isCategoriesAllOrNone(
        params.newSelectedValues.category,
        params.allCategories ?? []
      )
    ) {
      params.dispatchFilters(updateMultiSelectFilters({}));
      return;
    }


    let isFacetTrimingCatalog = checkFacetTrimmingCatalog(
      params.newSelectedValues,
      params.allCategories
    );

    const updatedMultiSelectedFilters = buildUpdatedMultiSelectedFilters(
      params,
      isFacetTrimingCatalog
    );

    const updatedFilters = buildUpdatedFilters(
      params,
      updatedMultiSelectedFilters
    );

    // Store in productListSlice the information about what filters the user select
    // Store the payload brought from SearchItems endpoint call
    params.dispatchFilters(updateFiltersRedux(updatedFilters));

    // Trigger SearchItems endpoint
    // Endpoint Responsibility: Show the product item list filtered according with what the user selected in the filter
    params.setIsInitialPageLoad(true);
    params.dispatchFilters(
      updateMultiSelectFilters(updatedMultiSelectedFilters)
    );

    if (params.subSearch) {
      await params.dispatchFilters(updateisMultiSelectFiltersApplied(true));
    } else {
      params.dispatchFilters(updateisMultiSelectFiltersApplied(true));
    }
  } else {
    // Only reset when no active filters are selected (user cleared all filters)
    resetFilters(params.dispatchFilters);
  }

};


function resetFilters(dispatch: any) {
    return Promise.all([
        dispatch(updateisMultiSelectFiltersApplied(false)),
        dispatch(updateMultiSelectFilters(null)),
    ]);
}

function buildSelectedValuesStore(filtersStored: any) {
    return {
        make: filtersStored?.make ?? [],
        year: filtersStored?.year ?? [],
        model: filtersStored?.model ?? [],
        component: filtersStored?.component ?? [],
        supplier: filtersStored?.supplier ?? [],
        category: filtersStored?.category ?? [],
    };
}

function hasActiveFilters(
    newSelectedValues: any,
    selectedValuesStore: any,
    extracted: any,
    minPrice: number,
    maxPrice: number
): boolean {
    return (
        (Object.values(newSelectedValues) as any[][]).reduce(
            (acc: number, curr: any[]) => acc + curr.length,
            0
        ) !== 0 ||
        (extracted.filtersStored &&
            (Object.values(selectedValuesStore) as any[][]).reduce(
                (acc: number, curr: any[]) => acc + curr.length,
                0
            ) !== 0) ||
        extracted.onSale ||
        extracted.newProduct ||
        extracted.callForPricing ||
        (extracted.priceRange[0] !== minPrice &&
            extracted.priceRange[0] != -1) ||
        (extracted.priceRange[1] !== maxPrice &&
            extracted.priceRange[1] != 1000000)
    );
}

export function buildUpdatedMultiSelectedFilters(
    params: any,
    isFacetTrimingCatalog: boolean
) {
    const categories: Category[] = transformCategories(
        params.allInitialCategoriesWithChildren,
        params.newSelectedValues.category,
        params.searchedLabel.category
    );
    let makes = [...params.newSelectedValues.make];
    let years = [...params.newSelectedValues.year];
    let models = [...params.newSelectedValues.model];
    let components = [...params.newSelectedValues.component];

    if (categories.length > 0) {
        const selectedAllESSTCategories = categories.some(
            (c) =>
                c.CategoryName === 'Essential Special Service Tools' &&
                c.SubCategories.length === 0
        );


    if (selectedAllESSTCategories) {
      makes = [];
      years = [];
      models = [];
      components = [];
    }
  }


    return {
        OnSale: params.onSale,
        NewProduct: params.newProduct,
        MinPrice: params.priceRange[0] == -1 ? 0 : params.priceRange[0],
        MaxPrice: params.priceRange[1] == 1000000 ? 0 : params.priceRange[1],
        Categories: isFacetTrimingCatalog ? [] : categories,
        Suppliers: params.newSelectedValues.supplier,
        Makes: makes,
        Years: years,
        Models: models,
        Components: components,
        callForPricing: params.callForPricing,
        isFacetTrimingCatalog,
    };
}

function buildUpdatedFilters(params: any, updatedMultiSelectedFilters: any) {
    return {
        searchedValue: params.searchParam,
        selectedFilters: {
            make: params.newSelectedValues.make,
            year: params.newSelectedValues.year,
            model: params.newSelectedValues.model,
            component: params.newSelectedValues.component,
            supplier: params.newSelectedValues.supplier,
            category: params.newSelectedValues.category,
            isOnSaleClicked: params.onSale,
            isNewClicked: params.newProduct,
            isCallForPricingClicked: params.callForPricing,
            minPrice: params.priceRange[0] == -1 ? 0 : params.priceRange[0],
            maxPrice:
                params.priceRange[1] == 1000000 ? 0 : params.priceRange[1],
            previousMinPrice: params.previousMinPrice,
            previousMaxPrice: params.previousMaxPrice,
        },
        apiPayload: updatedMultiSelectedFilters,
    };
}
